import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export type SeoSettings = {
  siteTitle: string;
  titleTemplate: string; // e.g. "%s | StoreSEO AI"
  description: string;
  keywords: string;
  ogImage: string;
  twitterHandle: string;
  canonicalBase: string;
  jsonLd: string; // raw JSON-LD string
  pages: Record<string, { title?: string; description?: string; keywords?: string; ogImage?: string }>;
};

const defaultSeo: SeoSettings = {
  siteTitle: "StoreSEO AI - AI-powered SEO for e-commerce",
  titleTemplate: "%s | StoreSEO AI",
  description:
    "Generate high-converting, SEO-optimized product titles, descriptions, keywords and tags in seconds with AI.",
  keywords: "seo, ecommerce seo, product seo, ai seo tool, shopify seo",
  ogImage: "",
  twitterHandle: "",
  canonicalBase: "https://storeseoai.com",
  jsonLd: "",
  pages: {},
};

let cached: SeoSettings | null = null;
const subs = new Set<(s: SeoSettings) => void>();

async function loadSeo(): Promise<SeoSettings> {
  const { data } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", "seo")
    .maybeSingle();
  const v = (data?.value as any) || {};
  const merged = { ...defaultSeo, ...v, pages: { ...defaultSeo.pages, ...(v.pages || {}) } };
  cached = merged;
  subs.forEach((cb) => cb(merged));
  return merged;
}

export function useSeo() {
  const [seo, setSeo] = useState<SeoSettings>(cached || defaultSeo);
  useEffect(() => {
    if (!cached) loadSeo();
    else setSeo(cached);
    const cb = (s: SeoSettings) => setSeo(s);
    subs.add(cb);
    return () => {
      subs.delete(cb);
    };
  }, []);
  return seo;
}

export function refreshSeo() {
  return loadSeo();
}

type Props = {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  noindex?: boolean;
};

export default function SeoHead(props: Props) {
  const seo = useSeo();
  const { pathname } = useLocation();
  const pageOverride = seo.pages?.[pathname] || {};

  const title = props.title || pageOverride.title || seo.siteTitle;
  const fullTitle =
    props.title || pageOverride.title
      ? seo.titleTemplate
        ? seo.titleTemplate.replace("%s", title)
        : title
      : seo.siteTitle;
  const description = props.description || pageOverride.description || seo.description;
  const keywords = props.keywords || pageOverride.keywords || seo.keywords;
  const image = props.image || pageOverride.ogImage || seo.ogImage;
  const base = seo.canonicalBase || "https://storeseoai.com";
  const canonical = `${base}${pathname}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {props.noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={props.type || "website"} />
      <meta property="og:url" content={canonical} />
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
      {seo.twitterHandle && <meta name="twitter:site" content={seo.twitterHandle} />}
      {seo.jsonLd && (
        <script type="application/ld+json">{seo.jsonLd}</script>
      )}
    </Helmet>
  );
}
